import React, { useEffect, useMemo, useState } from 'react';
import { Channel, User, OnlineUser, PresenceStatus } from '../types';
import { findDmPartner } from '../utils/presence';
import { ChannelListSkeleton } from './Skeleton';
import { classifyChannels } from '../utils/channelSections';
import { ChannelListItem } from './ChannelListItem';
import { matchesChannelQuery } from '../utils/channelSearch';
import { useChannelMemberIndex } from '../hooks/useChannelMemberIndex';
import { ContextMenu } from './ContextMenu';
import { buildChannelMenuItems } from './channelContextMenu';
import { PullToRefresh } from './PullToRefresh';
import { Plus, Bookmark, Search, X } from 'lucide-react';

interface ChatListScreenProps {
  channels: Channel[];
  unreadCounts: Record<number, number>;
  currentUser: User;
  onlineUsers?: OnlineUser[];
  onChannelSelect: (channel: Channel) => void;
  onTogglePin?: (channel: Channel) => void;
  onToggleMute?: (channel: Channel) => void;
  /** 컨텍스트 메뉴 '대화방 이름 변경' (group만 노출). 입력 모달은 App이 담당 */
  onRenameChannel?: (channel: Channel) => void;
  /** 컨텍스트 메뉴 '채팅방 나가기' (group/dm만 노출). 확인 모달은 App이 담당 */
  onLeaveChannel?: (channel: Channel) => void;
  onCreateGroup?: () => void;
  /** self 채널이 아직 없을 때 '나와의 채팅' 고정 항목 클릭 → 생성 (Sidebar와 동일 동작) */
  onSelfChatClick?: () => void;
  /** 데스크톱 3컬럼에서 현재 열린 채널 행 하이라이트. 모바일(전체화면 전환)에서는 미전달 */
  activeChannelId?: number | null;
  /** 채널 목록을 한 번이라도 받았는지 — false면 빈 문구 대신 스켈레톤 */
  channelsSynced?: boolean;
  /** 모바일 인스턴스는 CSS로만 숨는다(언마운트 안 됨) — false가 되면 열린 메뉴를 닫는다. 데스크톱은 미전달 */
  isVisible?: boolean;
  /**
   * 처음 고를 필터. 마운트 시에만 읽는다 — 데스크톱 목록은 rail 탭을 바꿀 때마다 새로 마운트되므로
   * 홈 '안읽음 보기'가 이 값으로 '안읽음'을 골라 둔 채 연다.
   */
  initialFilter?: ListFilter;
  /**
   * 필터 전환 요청(모바일 홈 '안읽음 보기'). 모바일 인스턴스는 CSS로만 숨어 계속 마운트돼 있어
   * initialFilter가 다시 읽히지 않으므로, 요청 객체가 바뀔 때마다 적용한다(seq로 같은 필터도 다시 요청).
   */
  filterRequest?: { filter: ListFilter; seq: number } | null;
}

/** 목록 상단 세그먼트. '그룹'은 type === 'group'만 — 공지(public)·1:1(dm)·나와의 채팅(self)은 제외 */
export type ListFilter = 'all' | 'unread' | 'group';
const FILTERS: { key: ListFilter; label: string; title: string }[] = [
  { key: 'all', label: '전체', title: '모든 대화방 보기' },
  { key: 'unread', label: '안읽음', title: '안읽은 대화만 보기' },
  { key: 'group', label: '그룹', title: '그룹 대화방만 보기' },
];

/**
 * 모바일 '대화방' 탭 전용 카카오톡식 목록 화면 (흰 배경, 아바타 + 2줄 행).
 * 데스크톱 Sidebar와 화면은 분리하되 분류/정렬 규칙(classifyChannels)은 공유한다.
 * 카카오톡처럼 단일 목록: 나와의 채팅 → 고정 채널(고정 최신순) → 나머지(최근 대화순).
 */
const ChatListScreenComponent: React.FC<ChatListScreenProps> = ({
  channels,
  unreadCounts,
  currentUser,
  onlineUsers = [],
  onChannelSelect,
  onTogglePin,
  onToggleMute,
  onRenameChannel,
  onLeaveChannel,
  onCreateGroup,
  onSelfChatClick,
  activeChannelId = null,
  channelsSynced = true,
  isVisible = true,
  initialFilter = 'all',
  filterRequest = null,
}) => {
  // 대화방 검색(방 이름·참여자·설명) + 목록 필터. 컴포넌트 로컬 state —
  // 모바일/데스크톱 인스턴스가 별개로 마운트되므로 각자 독립, 언마운트 시 자연 초기화
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ListFilter>(initialFilter);
  // 검색창은 헤더 돋보기 토글로만 노출 (카카오톡 패턴). 닫으면 검색어도 초기화
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const toggleSearch = () => {
    setIsSearchOpen(prev => {
      if (prev) setQuery('');
      return !prev;
    });
  };
  // 홈 '안읽음 보기' — 남아 있던 검색어가 안 읽은 방을 가리지 않게 검색도 닫는다
  useEffect(() => {
    if (!filterRequest) return;
    setFilter(filterRequest.filter);
    setIsSearchOpen(false);
    setQuery('');
  }, [filterRequest]);
  const isFiltering = query.trim() !== '' || filter !== 'all';
  // 참여자 이름 검색용 인덱스 — 검색창을 열 때만 1회 받아 모듈 캐시에 둔다
  const memberIndex = useChannelMemberIndex(isSearchOpen);

  const { list, hasSelfChannel } = useMemo(() => {
    const { selfChannel, mainChannels, groupChannels, dmChannels } =
      classifyChannels(channels, currentUser.affiliateId);
    const merged = [...mainChannels, ...groupChannels, ...dmChannels].sort((a, b) =>
      Number(b.isPinned ?? false) - Number(a.isPinned ?? false)
      || (b.isPinned
        ? (b.pinnedAt || '').localeCompare(a.pinnedAt || '')
        : (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''))
    );
    return {
      list: selfChannel ? [selfChannel, ...merged] : merged,
      hasSelfChannel: !!selfChannel,
    };
  }, [channels, currentUser.affiliateId]);

  const filteredList = useMemo(() => {
    if (!isFiltering) return list;
    const q = query.trim().toLowerCase();
    return list.filter(ch =>
      matchesChannelQuery(ch, q, memberIndex)
      && (filter !== 'unread' || (unreadCounts[ch.id] || 0) > 0)
      && (filter !== 'group' || ch.type === 'group')
    );
  }, [list, isFiltering, query, filter, unreadCounts, memberIndex]);

  // DM 상대 접속 상태: dmPartnerId(사번) 매칭 우선, 없으면 이름 매칭 폴백 (구버전 서버 호환).
  // 목록에 없는 상대는 오프라인이다 — 서버가 오프라인 사용자를 onlineUsers에서 지우기 때문.
  const dmPresenceById = useMemo(() => {
    const map = new Map<number, PresenceStatus>();
    for (const ch of list) {
      if (ch.type !== 'dm') continue;
      map.set(ch.id, findDmPartner(ch, onlineUsers)?.status ?? 'offline');
    }
    return map;
  }, [list, onlineUsers]);

  // 롱프레스 액션 메뉴 (self 채널 제외)
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; channel: Channel } | null>(null);
  const handleLongPress = (channel: Channel, pos: { x: number; y: number }) => {
    if (channel.type === 'self') return;
    setCtxMenu({ x: pos.x, y: pos.y, channel });
  };
  // 모바일 인스턴스는 탭을 떠나도 언마운트되지 않고 CSS로만 숨는다. 메뉴는 #root 포털이라
  // 부모의 display:none을 따라가지 않으므로, 숨는 순간 직접 닫지 않으면 홈 화면 위에 남는다
  useEffect(() => {
    if (!isVisible) setCtxMenu(null);
  }, [isVisible]);

  return (
    <div className="flex flex-col h-full bg-white safe-area-top select-none">
      {/* 헤더 — 우측 채팅방 헤더(h-14 md:h-16)와 높이 일치 */}
      <div className="h-14 md:h-16 flex items-center justify-between px-4 border-b border-slate-100 shrink-0">
        <h1 className="text-lg font-bold text-slate-800 shrink-0">대화</h1>
        <div className="flex items-center shrink-0">
          <button
            onClick={toggleSearch}
            className={`p-2 rounded-lg transition-colors ${
              isSearchOpen
                ? 'text-brand-600 bg-brand-50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
            }`}
            title="대화방 검색"
          >
            <Search size={20} />
          </button>
          {onCreateGroup && (
            <button
              onClick={onCreateGroup}
              className="p-2 -mr-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              title="채팅방 개설"
            >
              <Plus size={20} />
            </button>
          )}
        </div>
      </div>

      {/* 전체 / 안읽음 / 그룹 필터 세그먼트 — 헤더 한 줄에 함께 두면 좁은 목록 패널(md 288px)에서 넘친다 */}
      <div className="px-4 py-2 shrink-0">
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-full p-0.5">
          {FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
              title={f.title}
              className={`flex-1 h-8 rounded-full text-xs font-medium transition-colors ${
                filter === f.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* 검색창 (돋보기 토글) */}
      {isSearchOpen && (
        <div className="flex items-center px-4 py-2 border-b border-slate-100 shrink-0">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="이름, 참여자, 설명으로 찾기"
              aria-label="이름, 참여자, 설명으로 대화방 찾기"
              autoFocus
              className="w-full pl-9 pr-8 py-2 bg-slate-100 rounded-lg text-base md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-md text-slate-400 hover:text-slate-600"
                title="지우기"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* 목록 — 터치 기기에서는 상단에서 당기면 새로고침(PWA에는 주소창이 없다) */}
      <PullToRefresh className="flex-1 overflow-y-auto">
        {/* self 채널이 아직 없으면 고정 진입점 표시 (첫 클릭 시 생성). 필터 중에는 숨김 */}
        {!hasSelfChannel && onSelfChatClick && !isFiltering && (
          <button
            onClick={onSelfChatClick}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left border-b border-slate-50"
          >
            <span className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
              <Bookmark size={22} className="text-slate-400" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="text-sm font-bold text-slate-800">나와의 채팅</span>
              <span className="block text-sm text-slate-400 mt-0.5">메모·파일을 나에게 보내 보관하세요</span>
            </span>
          </button>
        )}
        {!channelsSynced && filteredList.length === 0 ? (
          <ChannelListSkeleton rows={6} size="lg" />
        ) : filteredList.length === 0 && (isFiltering || hasSelfChannel || !onSelfChatClick) ? (
          <p className="px-4 py-8 text-sm text-slate-400 text-center">
            {query.trim() ? '검색 결과가 없습니다'
              : filter === 'unread' ? '안읽은 대화가 없습니다'
              : filter === 'group' ? '그룹 대화방이 없습니다'
              : '대화방이 없습니다'}
          </p>
        ) : (
          <div className="divide-y divide-slate-50">
            {filteredList.map(ch => (
              <ChannelListItem
                key={ch.id}
                channel={ch}
                unreadCount={unreadCounts[ch.id] || 0}
                onSelect={onChannelSelect}
                onLongPress={handleLongPress}
                onContextMenu={handleLongPress}
                size="lg"
                presenceStatus={dmPresenceById.get(ch.id)}
                isActive={ch.id === activeChannelId}
              />
            ))}
          </div>
        )}
      </PullToRefresh>

      {/* 롱프레스 액션 메뉴 (Sidebar 우클릭 메뉴와 동일 항목) */}
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          variant="light"
          backdrop
          onClose={() => setCtxMenu(null)}
          items={buildChannelMenuItems(
            ctxMenu.channel,
            { onTogglePin, onToggleMute, onRenameChannel, onLeaveChannel },
            () => setCtxMenu(null),
          )}
        />
      )}
    </div>
  );
};

export const ChatListScreen = React.memo(ChatListScreenComponent);
